import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, CardContent, IconButton, Paper, Stack, Typography } from '@mui/material';
import { Close, DragIndicator } from '@mui/icons-material';
import type { PageConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';
import type { TranslationPath } from 'c-translation';
import { Translate, useCommonTranslation } from 'c-translation';
import { Button, ListItemIcon, ListItemText, TransparentCard } from 'c-components';
import { useAuthenticatedUser, useAPIClientRequest } from 'c-hooks';
import ApiClient from 'c-data/apiClient';
import { useEntityData } from 'c-data';
import type { User } from 'c-entity-types';
import { useDispatch } from 'react-redux';
import { authActions } from 'c-auth-module/Slices/Auth';
import UserEditPasswordWrapper from 'c-admin/Components/User/UserEditPassword';
import ReactGA from 'react-ga';
import {
    analogueListIcon,
    agencyIcon,
    buyerIcon,
    clientIcon,
    countriesListIcon,
    dataStatusIcon,
    dataStatusLineItemsIcon,
    displaysListIcon,
    frameSearchIcon,
    logFileImportIcon,
    logFileSourceIcon,
    marketsIcon,
    notificationsIcon,
    ownerListIcon,
    packsListIcon,
    roleIcon,
    skinIcon,
    tagsListIcon,
    taskQueueIcon,
    userIcon,
    virtualCampaignBuilderIcon,
    displaysIcon,
} from 'c-main/icons';

type AdminFavouriteOption = {
    route: string;
    label: string;
    icon: React.ReactNode;
};

type DraggingData = {
    route: string;
    source: 'pool' | 'slot';
    slotIndex?: number;
};

const maxFavourites = 3;

const getAdminNavOptions = (): AdminFavouriteOption[] => [
    {
        route: PostAuthRoutes.Campaigns.DataStatus,
        label: 'Navigation.dataStatus',
        icon: dataStatusIcon,
    },
    {
        route: PostAuthRoutes.admin.DataStatusLineItems,
        label: 'Navigation.dataStatusLineItems',
        icon: dataStatusLineItemsIcon,
    },
    {
        route: PostAuthRoutes.Admin.NotificationsList,
        label: 'Navigation.adminNotifications',
        icon: notificationsIcon,
    },
    { route: PostAuthRoutes.Client.ClientList, label: 'Navigation.clients', icon: clientIcon },
    { route: PostAuthRoutes.Agency.AgencyList, label: 'Navigation.agencies', icon: agencyIcon },
    { route: PostAuthRoutes.Buyer.BuyerList, label: 'Navigation.buyers', icon: buyerIcon },
    { route: PostAuthRoutes.Admin.SkinList, label: 'Navigation.adminSkins', icon: skinIcon },
    { route: PostAuthRoutes.Admin.MarketList, label: 'Navigation.adminMarkets', icon: marketsIcon },
    { route: PostAuthRoutes.Admin.UserList, label: 'Navigation.adminUsers', icon: userIcon },
    { route: PostAuthRoutes.Admin.RoleList, label: 'Navigation.adminRoles', icon: roleIcon },
    {
        route: PostAuthRoutes.Admin.ImpersonateUser,
        label: 'Navigation.adminImpersonateUser',
        icon: userIcon,
    },
    {
        route: PostAuthRoutes.Admin.FrameSearch,
        label: 'Navigation.adminFrameSearch',
        icon: frameSearchIcon,
    },
    {
        route: PostAuthRoutes.Admin.TaskQueue,
        label: 'Navigation.popDisplaysTaskQueue',
        icon: taskQueueIcon,
    },
    {
        route: PostAuthRoutes.PopDisplays.VirtualCampaignBuilder,
        label: 'Navigation.popDisplaysVirtualCampaignBuilder',
        icon: virtualCampaignBuilderIcon,
    },
    {
        route: PostAuthRoutes.PopDisplays.ChannelFrameList,
        label: 'Navigation.popDisplaysChannelFrames',
        icon: displaysIcon,
    },
    {
        route: PostAuthRoutes.PopDisplays.LogFileImportList,
        label: 'Navigation.popDisplaysLogFileImports',
        icon: logFileImportIcon,
    },
    {
        route: PostAuthRoutes.PopDisplays.LogImportSource,
        label: 'Navigation.popDisplaysLogImportSource',
        icon: logFileSourceIcon,
    },
    {
        route: PostAuthRoutes.Displays.DisplaysList,
        label: 'Navigation.displaysDisplayList',
        icon: displaysListIcon,
    },
    {
        route: PostAuthRoutes.Displays.PackList,
        label: 'Navigation.displaysPacks',
        icon: packsListIcon,
    },
    {
        route: PostAuthRoutes.Displays.OwnerList,
        label: 'Navigation.displaysOwnerList',
        icon: ownerListIcon,
    },
    {
        route: PostAuthRoutes.Displays.AnaloguesList,
        label: 'Navigation.displaysAnalogue',
        icon: analogueListIcon,
    },
    {
        route: PostAuthRoutes.Displays.TagsList,
        label: 'Navigation.displaysTag',
        icon: tagsListIcon,
    },
    {
        route: PostAuthRoutes.Displays.CountryList,
        label: 'Navigation.displaysCountry',
        icon: countriesListIcon,
    },
];

const MyProfilePage = () => {
    const t = useCommonTranslation();
    const dispatch = useDispatch();

    useEffect(() => {
        ReactGA.pageview(window.location.pathname + window.location.search);
    }, []);

    const { user, isAdmin } = useAuthenticatedUser();
    const { upsertEntity } = useEntityData<User>('User');
    const [favouriteSlots, setFavouriteSlots] = useState<(string | null)[]>(
        Array(maxFavourites).fill(null),
    );
    const [draggingData, setDraggingData] = useState<DraggingData | null>(null);

    const {
        start: saveFavourites,
        isLoading,
        error,
        hasSucceeded,
    } = useAPIClientRequest(ApiClient.Entities.User.updateFavourites);
    const { start: getMe } = useAPIClientRequest(ApiClient.Entities.User.me);

    const options = useMemo(() => getAdminNavOptions(), []);
    const optionMap = useMemo(
        () =>
            options.reduce(
                (acc, option) => ({ ...acc, [option.route]: option }),
                {} as Record<string, AdminFavouriteOption>,
            ),
        [options],
    );

    useEffect(() => {
        const safeFavourites = (user?.favourites ?? []).filter(route => Boolean(optionMap[route]));
        setFavouriteSlots([
            safeFavourites[0] ?? null,
            safeFavourites[1] ?? null,
            safeFavourites[2] ?? null,
        ]);
    }, [optionMap, user?.favourites]);

    const selectedFavourites = useMemo(
        () => favouriteSlots.filter(Boolean) as string[],
        [favouriteSlots],
    );

    const availableOptions = useMemo(
        () => options.filter(option => !selectedFavourites.includes(option.route)),
        [options, selectedFavourites],
    );

    const hasChanges = useMemo(() => {
        const current = (user?.favourites ?? [])
            .filter(route => Boolean(optionMap[route]))
            .slice(0, maxFavourites);
        return JSON.stringify(current) !== JSON.stringify(selectedFavourites);
    }, [optionMap, selectedFavourites, user?.favourites]);

    const onSaveFavourites = useCallback(async () => {
        if (!user) return;

        try {
            const payload = selectedFavourites.slice(0, maxFavourites);
            const response = await saveFavourites(user.id, payload);
            const userFromUpdate =
                (response?.data?.data as unknown as User | undefined) ??
                ({
                    ...user,
                    favourites: payload,
                } as User);
            const meResponse = await getMe();
            const updatedUser =
                (meResponse?.data?.data as unknown as User | undefined) ?? userFromUpdate;

            upsertEntity(updatedUser);
            dispatch(authActions.finishedValidatingSession({ user: updatedUser }));
        } catch {
            // Errors are surfaced via hook state.
        }
    }, [dispatch, getMe, saveFavourites, selectedFavourites, upsertEntity, user]);

    const onDragStart = useCallback(
        (route: string, source: DraggingData['source'], slotIndex?: number) => () =>
            setDraggingData({ route, source, slotIndex }),
        [],
    );

    const onDragEnd = useCallback(() => setDraggingData(null), []);

    const onDragOver = useCallback((event: React.DragEvent) => {
        event.preventDefault();
    }, []);

    const onDropSlot = useCallback(
        (targetSlotIndex: number) => (event: React.DragEvent) => {
            event.preventDefault();
            if (!draggingData) return;

            setFavouriteSlots(prev => {
                const next = [...prev];
                const route = draggingData.route;

                if (draggingData.source === 'slot' && draggingData.slotIndex != null) {
                    const fromSlotIndex = draggingData.slotIndex;
                    if (fromSlotIndex === targetSlotIndex) {
                        return prev;
                    }

                    const targetValue = next[targetSlotIndex];
                    next[targetSlotIndex] = route;
                    next[fromSlotIndex] = targetValue ?? null;
                    return next;
                }

                const existingIndex = next.indexOf(route);
                if (existingIndex >= 0) {
                    const targetValue = next[targetSlotIndex];
                    next[targetSlotIndex] = route;
                    next[existingIndex] = targetValue ?? null;
                    return next;
                }

                next[targetSlotIndex] = route;
                return next;
            });

            setDraggingData(null);
        },
        [draggingData],
    );

    const onDropPool = useCallback(
        (event: React.DragEvent) => {
            event.preventDefault();
            if (!draggingData) return;

            if (draggingData.source === 'slot' && draggingData.slotIndex != null) {
                setFavouriteSlots(prev => {
                    const next = [...prev];
                    next[draggingData.slotIndex as number] = null;
                    return next;
                });
            }

            setDraggingData(null);
        },
        [draggingData],
    );

    const onRemoveFavourite = useCallback((slotIndex: number) => {
        setFavouriteSlots(prev => {
            const next = [...prev];
            next[slotIndex] = null;
            return next;
        });
    }, []);

    return (
        <TransparentCard title={t('Modules.Main.MyProfile.metaTitle')}>
            <CardContent>
                {isAdmin && (
                    <Stack spacing={2} mt={4}>
                        <Typography variant="h6">
                            {t('Modules.Main.MyProfile.favouritesTitle')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('Modules.Main.MyProfile.favouritesDescription')}
                        </Typography>

                        <Typography variant="subtitle2">
                            {t('Modules.Main.MyProfile.favouritesOrderTitle')}
                        </Typography>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                            {favouriteSlots.map((route, index) => {
                                const option = route ? optionMap[route] : null;
                                return (
                                    <Paper
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={`slot-${index}`}
                                        variant="outlined"
                                        onDragOver={onDragOver}
                                        onDrop={onDropSlot(index)}
                                        sx={{
                                            p: 1,
                                            minHeight: 74,
                                            minWidth: 230,
                                            flex: 1,
                                            borderStyle: 'dashed',
                                            borderColor: route ? 'divider' : 'primary.main',
                                            bgcolor: route ? 'background.paper' : 'action.hover',
                                        }}
                                    >
                                        {!option && (
                                            <Typography variant="body2" color="text.secondary">
                                                {t(
                                                    'Modules.Main.MyProfile.emptyFavouriteSlotLabel',
                                                    {
                                                        index: index + 1,
                                                    },
                                                )}
                                            </Typography>
                                        )}
                                        {option && (
                                            <Box
                                                draggable
                                                onDragStart={onDragStart(
                                                    option.route,
                                                    'slot',
                                                    index,
                                                )}
                                                onDragEnd={onDragEnd}
                                                sx={{ cursor: 'grab' }}
                                            >
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    spacing={1}
                                                >
                                                    <DragIndicator fontSize="small" />
                                                    <ListItemIcon
                                                        sx={{ fontSize: '1.4em', minWidth: 32 }}
                                                    >
                                                        {option.icon}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={
                                                            <Translate
                                                                path={
                                                                    option.label as TranslationPath
                                                                }
                                                            />
                                                        }
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onRemoveFavourite(index)}
                                                    >
                                                        <Close fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </Box>
                                        )}
                                    </Paper>
                                );
                            })}
                        </Stack>

                        <Typography variant="subtitle2">
                            {t('Modules.Main.MyProfile.favouritesAvailableTitle')}
                        </Typography>
                        <Stack
                            direction="row"
                            flexWrap="wrap"
                            gap={1}
                            onDragOver={onDragOver}
                            onDrop={onDropPool}
                            sx={{
                                border: '1px dashed',
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 1,
                            }}
                        >
                            {availableOptions.map(option => (
                                <Paper
                                    key={option.route}
                                    variant="outlined"
                                    draggable
                                    onDragStart={onDragStart(option.route, 'pool')}
                                    onDragEnd={onDragEnd}
                                    sx={{ px: 1.5, py: 1, minWidth: 220, cursor: 'grab' }}
                                >
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <ListItemIcon sx={{ fontSize: '1.4em', minWidth: 32 }}>
                                            {option.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Translate path={option.label as TranslationPath} />
                                            }
                                        />
                                        <DragIndicator fontSize="small" />
                                    </Stack>
                                </Paper>
                            ))}
                            {availableOptions.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    {t('Modules.Main.MyProfile.noMoreAvailableLabel')}
                                </Typography>
                            )}
                        </Stack>

                        <Typography variant="body2" color="text.secondary">
                            {t('Modules.Main.MyProfile.favouritesMaxLabel', {
                                count: maxFavourites,
                            })}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('Modules.Main.MyProfile.favouritesPoolHint')}
                        </Typography>

                        <Stack direction="row" spacing={2} alignItems="center">
                            <Button
                                variant="contained"
                                onClick={onSaveFavourites}
                                disabled={!hasChanges || isLoading}
                            >
                                {t('Modules.Main.MyProfile.saveFavouritesLabel')}
                            </Button>
                            {error && (
                                <Typography variant="body2" color="error">
                                    {String(error)}
                                </Typography>
                            )}
                            {!error && hasSucceeded && (
                                <Typography variant="body2" color="success.main">
                                    {t('Modules.Main.MyProfile.favouritesSavedLabel')}
                                </Typography>
                            )}
                        </Stack>
                    </Stack>
                )}
                <UserEditPasswordWrapper id={user.id} />
            </CardContent>
        </TransparentCard>
    );
};

const setup: PageConfig = {
    id: 'MyProfilePage',
    component: MyProfilePage,
    systemSearch: {
        title: 'Modules.Main.MyProfile.metaTitle',
        description: 'Modules.Main.MyProfile.description',
        type: 'page',
        aliases: 'SystemSearch.aliases.myProfilePage',
        route: PostAuthRoutes.Users.MyProfile,
        saveAsRecentPage: true,
        breadcrumbs: [],
    },
};

export default setup;
